import type {Component, ComponentDefinition, Editor} from 'grapesjs';
import {getAssetUrl} from '@Utils/s3.util';
import {
  COMPONENT_LOADING_OVERLAY_CLASS,
  COMPONENT_LOADING_OVERLAY_WRAPPER_ID,
  EMAIL_MAKER_COMPONENT_TYPES,
  EMAIL_MAKER_PROPS,
  EMAIL_MEDIA_OVERLAY_ID,
  EMAIL_OVERLAY_ID,
  MOBILE_CSS_PROP_PREFIX,
  TABLET_CSS_PROP_PREFIX,
} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerCanvasDevice} from '@EmailMakerReact/email-maker-react.types';
import type {ComponentLoadingOverlayStyle} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {EmailOverlayStyle} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getMainWrapper} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {getReadOnlyFooter} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {getCurrentZoom} from '@EmailMakerReact/lib/email-maker-zoom-library';

/**
 * Canvas overlay handling for the React email maker.
 *
 * Overlay elements are DOM-level (never components, except the full-canvas overlay) and are
 * created/removed with the UndoManager stopped, so they never reach the save payload.
 */

/** Same value as the EMAIL_MAKER_PROPS.MEDIA_TYPE prop, used as a CSS class on media elements. */
const MEDIA_TYPE_CLASS = EMAIL_MAKER_PROPS.MEDIA_TYPE;

const MEDIA_OVERLAY_ICON_DISPLAY_THRESHOLD = 125;
const MEDIA_OVERLAY_TEXT_DISPLAY_THRESHOLD = 75;

const EMAIL_OVERLAY_CLASSES_MAP: Record<EmailOverlayStyle, string> = {
  [EmailOverlayStyle.LOADING]: '-loading',
  [EmailOverlayStyle.AI_EDIT]: '-ai-edit',
};

/** Returns the full-canvas loading/AI overlay component, when present. */
export function getLoadingOverlayForEmail(editor: Editor): Component | null {
  const overlay = editor.DomComponents.getWrapper()?.find(`#${EMAIL_OVERLAY_ID}`);

  if (overlay && overlay.length > 0) {
    return overlay[0];
  }

  return null;
}

/** Resizes the overlay to cover the wrapper plus footer. */
export function fixLoadingOverlayForEmailSize(editor: Editor): void {
  const overlay = getLoadingOverlayForEmail(editor);
  const wrapper = getMainWrapper(editor);
  const footer = getReadOnlyFooter(editor);

  if (!overlay || !wrapper || !footer) {
    return;
  }

  const wrapperHeight = wrapper.getEl()?.offsetHeight;
  const footerHeight = footer.getEl()?.offsetHeight;

  const height = wrapperHeight && footerHeight ? `${(wrapperHeight + footerHeight).toString()}px` : '100%';

  overlay.setStyle({height});
}

/** Shows/hides the full-canvas loading or AI-edit overlay, outside the undo history. */
export function toggleLoadingOverlayForEmail(editor: Editor, show: boolean, style: EmailOverlayStyle = EmailOverlayStyle.LOADING): void {
  const overlay = getLoadingOverlayForEmail(editor);
  if (show && overlay) {
    return;
  }

  if (!show && !overlay) {
    return;
  }

  editor.UndoManager.stop();

  if (show) {
    editor.addComponents(buildEmailOverlayDefinition(style), {at: 0});
    fixLoadingOverlayForEmailSize(editor);
  } else if (overlay) {
    editor.getComponents().remove(overlay);
  }

  editor.UndoManager.start();
}

/** Wraps/unwraps a media element with the dark "double click to add" hover overlay, outside the undo history. */
export function toggleMediaComponentOverlay(editor: Editor, element: HTMLElement | undefined, show: boolean): void {
  if (!element) {
    return;
  }

  editor.UndoManager.stop();

  const elementParent = element.parentNode as HTMLElement | null;
  const overlayWrapper = elementParent?.id === EMAIL_MEDIA_OVERLAY_ID ? elementParent : null;

  if (!show) {
    if (overlayWrapper?.parentNode) {
      overlayWrapper.parentNode.insertBefore(element, overlayWrapper);
      overlayWrapper.parentNode.removeChild(overlayWrapper);
    }

    editor.UndoManager.start();
    return;
  }

  if (!overlayWrapper) {
    const wrapper = document.createElement('div');
    wrapper.id = EMAIL_MEDIA_OVERLAY_ID;
    wrapper.style.position = 'relative';

    element.parentNode?.insertBefore(wrapper, element);
    wrapper.appendChild(element);

    const overlayContent = getMediaComponentOverlayContent(element);
    wrapper.insertBefore(overlayContent, element);
  }

  editor.UndoManager.start();
}

/** Shows/hides a per-component DOM loading overlay (special-cased for table cells). */
export function toggleComponentLoadingOverlay(editor: Editor, component: Component, show: boolean): void {
  const componentEl = component.getEl();
  if (!componentEl) {
    return;
  }

  const overlayId = getLoadingOverlayIdFromComponent(component);
  const isTableCell = componentEl.tagName === 'TD';

  if (show) {
    const overlayEl = createLoadingOverlayElement(overlayId);

    if (isTableCell) {
      showLoadingOverlayForTableCell(componentEl, overlayEl);
    } else {
      showLoadingOverlayForGeneralCase(editor, componentEl, overlayEl);
    }
  } else if (isTableCell) {
    hideLoadingOverlayForTableCell(componentEl, overlayId);
  } else {
    hideLoadingOverlayForGeneralCase(componentEl, overlayId);
  }
}

/** Clears overlays left stale by a previous session after load. */
export function removeComponentLoadingOverlaysOnLoad(editor: Editor): void {
  getAllComponentLoadingOverlays(editor).forEach((loadingOverlay) => {
    const componentId = getComponentIdFromLoadingOverlayElement(loadingOverlay);
    const component = editor.DomComponents.getWrapper()?.find(`#${componentId}`)[0];

    if (!component) {
      return;
    }

    toggleComponentLoadingOverlay(editor, component, false);
  });
}

/** Re-measures every loading overlay; run after device switches and window resizes. */
export function fixComponentLoadingOverlaysOnWindowResize(editor: Editor): void {
  getAllComponentLoadingOverlays(editor).forEach((loadingOverlay) => {
    const componentId = getComponentIdFromLoadingOverlayElement(loadingOverlay);
    const componentEl = editor.Canvas.getBody().querySelector<HTMLElement>(`#${componentId}`);

    if (!componentEl) {
      return;
    }

    Object.assign(loadingOverlay.style, getStylesForComponentLoadingOverlay(editor, componentEl));
  });
}

function buildEmailOverlayDefinition(style: EmailOverlayStyle): ComponentDefinition {
  return {
    attributes: {
      id: EMAIL_OVERLAY_ID,
      'data-is-email-overlay': true,
    },
    classes: EMAIL_OVERLAY_CLASSES_MAP[style],
    selectable: false,
    hoverable: false,
  };
}

function getAllComponentLoadingOverlays(editor: Editor): NodeListOf<HTMLElement> {
  return editor.Canvas.getBody().querySelectorAll<HTMLElement>(`.${COMPONENT_LOADING_OVERLAY_CLASS}`);
}

function getLoadingOverlayIdFromComponent(component: Component): string {
  return `${component.ccid}-${COMPONENT_LOADING_OVERLAY_CLASS}`;
}

/** Returns the component id encoded in a loading overlay element id. */
function getComponentIdFromLoadingOverlayElement(loadingOverlayEl: HTMLElement): string {
  return loadingOverlayEl.id.split('-')[0];
}

/** Returns styles for a component loading overlay from the component's element. */
function getStylesForComponentLoadingOverlay(editor: Editor, componentEl: HTMLElement): ComponentLoadingOverlayStyle {
  return {
    width: `${componentEl.clientWidth}px`,
    marginLeft: calculateMediaOverlayLeftMargin(editor, componentEl),
  };
}

function createLoadingOverlayElement(overlayId: string): HTMLDivElement {
  const overlayEl = document.createElement('div');
  overlayEl.id = overlayId;
  overlayEl.classList.add(COMPONENT_LOADING_OVERLAY_CLASS);
  overlayEl.setAttribute('draggable', 'false');
  overlayEl.setAttribute('selectable', 'false');
  overlayEl.setAttribute('hoverable', 'false');
  overlayEl.setAttribute('droppable', 'false');

  return overlayEl;
}

function showLoadingOverlayForTableCell(componentEl: HTMLElement, overlayEl: HTMLDivElement): void {
  componentEl.style.position = 'relative';
  Object.assign(overlayEl.style, {
    position: 'absolute',
    inset: '0',
  });

  componentEl.insertBefore(overlayEl, componentEl.firstChild);
}

function showLoadingOverlayForGeneralCase(editor: Editor, componentEl: HTMLElement, overlayEl: HTMLDivElement): void {
  const parentAlignment = componentEl.parentElement?.getAttribute('align') ?? null;

  const wrapper = document.createElement('div');
  wrapper.id = COMPONENT_LOADING_OVERLAY_WRAPPER_ID;

  componentEl.parentNode?.insertBefore(wrapper, componentEl);
  wrapper.appendChild(overlayEl);
  wrapper.appendChild(componentEl);
  if (parentAlignment) {
    wrapper.setAttribute('align', parentAlignment);
  }

  wrapper.style.position = 'relative';
  Object.assign(overlayEl.style, {
    position: 'absolute',
    inset: '0',
    'border-radius': componentEl.style.borderRadius,
  });

  Object.assign(overlayEl.style, getStylesForComponentLoadingOverlay(editor, componentEl));
}

function hideLoadingOverlayForTableCell(componentEl: HTMLElement, loadingOverlayId: string): void {
  const overlay = componentEl.querySelector(`#${loadingOverlayId}`);
  if (overlay) {
    overlay.remove();
    componentEl.style.position = '';
  }
}

function hideLoadingOverlayForGeneralCase(componentEl: HTMLElement, loadingOverlayId: string): void {
  const overlayWrapper = componentEl.parentNode as HTMLElement | null;
  const loadingOverlay = overlayWrapper?.firstChild as HTMLElement | null;
  if (overlayWrapper?.id === COMPONENT_LOADING_OVERLAY_WRAPPER_ID && loadingOverlay?.id === loadingOverlayId) {
    overlayWrapper.parentNode?.insertBefore(componentEl, overlayWrapper);
    overlayWrapper.parentNode?.removeChild(overlayWrapper);
  }
}

function calculateElementOverlayLeftMargin(element: HTMLElement): string {
  return window.getComputedStyle(element).marginLeft;
}

/** Derives the overlay's left margin from the element's percentage width, parent alignment, and current zoom. */
function calculateMediaOverlayLeftMargin(editor: Editor, element: HTMLElement): string {
  const zoomValue = getCurrentZoom(editor);
  const parentAlignment = element.parentElement?.getAttribute('align') ?? '';
  const width = getElementResponsiveWidth(editor, element);
  const percentageWidth = parseFloat(width);

  let marginLeft = calculateElementOverlayLeftMargin(element);

  if (isNaN(percentageWidth) || percentageWidth <= 0) {
    return `${parseInt(marginLeft, 10) * (1 / zoomValue)}px`;
  }

  const elementRect = element.getBoundingClientRect();

  switch (parentAlignment) {
    case 'center':
    case 'middle':
      marginLeft = `${elementRect.width * (50 / percentageWidth - 0.5)}px`;
      break;
    case 'right':
      marginLeft = `${elementRect.width * (100 / percentageWidth - 1)}px`;
      break;
    default:
      marginLeft = calculateElementOverlayLeftMargin(element);
      break;
  }

  return `${parseInt(marginLeft, 10) * (1 / zoomValue)}px`;
}

/** Returns the element's width for the selected device, read from inline device-prefixed props with media-type-rule and inline-style fallbacks. */
function getElementResponsiveWidth(editor: Editor, element: HTMLElement): string {
  const STYLE_SEPARATOR = ';';
  const STYLE_KEY_VALUE_SEPARATOR = ':';

  const deviceStylePrefix: Record<string, string> = {
    [EmailMakerCanvasDevice.DESKTOP]: '',
    [EmailMakerCanvasDevice.MOBILE]: MOBILE_CSS_PROP_PREFIX,
    [EmailMakerCanvasDevice.TABLET]: TABLET_CSS_PROP_PREFIX,
  };

  const selectedDevice = editor.Devices.getSelected()?.id;

  if (!selectedDevice) {
    return element.style.width;
  }

  let width: string | null = null;

  element.style.cssText.split(STYLE_SEPARATOR).forEach((style) => {
    const parts = style.split(STYLE_KEY_VALUE_SEPARATOR);
    if (parts.length === 2 && parts[0].trim() === `${deviceStylePrefix[selectedDevice]}width`) {
      width = parts[1];
    }
  });

  if (!width) {
    const mediaTypeRules = editor.Css.getRules(`.${MEDIA_TYPE_CLASS}`);
    const ruleWidth = element.classList.contains(MEDIA_TYPE_CLASS) && mediaTypeRules.length > 0 ? mediaTypeRules[0].getStyle().width : undefined;
    width = typeof ruleWidth === 'string' ? ruleWidth : element.style.width;
  }

  return width.replace(/!important/g, '').trim();
}

/** Builds the dark overlay div with the type-specific "double click" icon/text, size-gated by the media's rect. */
function getMediaComponentOverlayContent(mediaImage: HTMLElement): HTMLElement {
  const overlayDiv = document.createElement('div');

  Object.assign(overlayDiv.style, {
    position: 'absolute',
    left: `${mediaImage.offsetLeft}px`,
    top: '0',
    width: `${mediaImage.offsetWidth}px`,
    height: '100%',
    zIndex: 10,
    backgroundColor: 'rgba(20, 19, 37, 0.6)',
    pointerEvents: 'none',
    borderRadius: mediaImage.style.borderRadius,
    overflow: 'hidden',
  });

  const mediaRect = mediaImage.getBoundingClientRect();
  const showIcon = mediaRect.height > MEDIA_OVERLAY_ICON_DISPLAY_THRESHOLD && mediaRect.width > MEDIA_OVERLAY_ICON_DISPLAY_THRESHOLD;
  const showText = mediaRect.height > MEDIA_OVERLAY_TEXT_DISPLAY_THRESHOLD && mediaRect.width > MEDIA_OVERLAY_TEXT_DISPLAY_THRESHOLD;

  const contentContainer = document.createElement('div');
  const image = document.createElement('img');
  const text = document.createElement('p');

  image.src = getAssetUrl('email-maker-media-component-hover.png?t=1');

  const overlayTextKeyMap: Record<string, string> = {
    [EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO]: 'pmwjs_email_video_overlay_text',
    [EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER]: 'pmwjs_email_sticker_overlay_text',
  };
  const pmwType = mediaImage.getAttribute('data-pmw-type') ?? '';
  text.innerText = window.i18next.t(overlayTextKeyMap[pmwType] ?? 'pmwjs_email_media_overlay_text');

  Object.assign(contentContainer.style, {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: 'white',
    width: '100%',
  });

  Object.assign(image.style, {
    width: '100%',
    maxWidth: '40px',
    height: 'auto',
  });

  Object.assign(text.style, {
    padding: '0',
    margin: '0',
    color: '#FFF',
    'text-align': 'center',
    'font-family': '"Figtree", Helvetica, Arial, sans-serif',
    'font-size': showIcon ? '14px' : '12px',
    'font-style': 'normal',
    'font-weight': 400,
    'line-height': '150%',
  });

  if (showIcon) {
    contentContainer.appendChild(image);
  }
  if (showText) {
    contentContainer.appendChild(text);
  }

  overlayDiv.appendChild(contentContainer);

  return overlayDiv;
}
